import React, { useState, useEffect, useMemo } from 'react'
import { useData } from '../contexts/DataContext'
import { generateId } from '../utils/helpers'

const Builder = () => {
  const { exercises, fetchExercises, createExercise, updateExercise, deleteExercise } = useData()
  
  const [tab, setTab] = useState('strength')
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('lbs')
  const [desc, setDesc] = useState('')
  const [metric, setMetric] = useState('distance')
  const [firstSub, setFirstSub] = useState('')
  const [showInKiosk, setShowInKiosk] = useState(true)
  const [firstFields, setFirstFields] = useState({
    reps: true,
    weight: true,
    sets: true,
    time: false,
    notes: true,
    primary: true
  })

  useEffect(() => {
    fetchExercises()
  }, [])

  useEffect(() => {
    if (tab === 'strength') {
      setUnit('lbs')
      setFirstFields({ reps: true, weight: true, sets: true, time: false, notes: true, primary: false })
    }
    if (tab === 'cardio') {
      setUnit('m')
      setMetric('distance')
      setFirstFields({ reps: false, weight: false, sets: false, time: true, notes: true, primary: true })
    }
    if (tab === 'workout') {
      setUnit('lbs')
      setFirstFields({ reps: true, weight: true, sets: true, time: false, notes: true, primary: false })
    }
  }, [tab])

  const handleAddExercise = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      alert('Please add a name.')
      return
    }

    const exerciseData = {
      name: trimmedName,
      mode: tab,
      unit,
      showInKiosk
    }

    if (tab === 'cardio') {
      exerciseData.cardioMetric = metric
    }

    if (tab === 'workout') {
      exerciseData.desc = desc.trim()
    }

    // Add first subcategory if specified
    if (firstSub.trim() || tab === 'workout') {
      const subName = firstSub.trim() || 'Main'
      exerciseData.subs = [{
        name: subName,
        showInKiosk: true,
        fields: { ...firstFields }
      }]
    }

    const result = await createExercise(exerciseData)
    if (result.success) {
      setName('')
      setDesc('')
      setFirstSub('')
    } else {
      alert(result.error)
    }
  }

  const sorted = useMemo(() => 
    exercises.filter(e => e.mode === tab).sort((a, b) => a.name.localeCompare(b.name)),
    [exercises, tab]
  )

  const handleAddSub = async (exercise) => {
    const subName = prompt("Subcategory name (e.g., '10 Reps', 'Week 1', 'Main')")
    if (!subName) return

    const defaultFields = tab === 'cardio' 
      ? { primary: true, time: true, notes: true }
      : { reps: true, weight: true, sets: true, notes: true }

    const newSub = {
      name: subName,
      showInKiosk: true,
      fields: defaultFields
    }

    const updatedSubs = [...(exercise.subs || []), newSub]
    const result = await updateExercise(exercise._id, { subs: updatedSubs })
    
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleDeleteExercise = async (exerciseId) => {
    if (!confirm('Are you sure you want to delete this exercise?')) return
    
    const result = await deleteExercise(exerciseId)
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleToggleKiosk = async (exercise, showInKiosk) => {
    const result = await updateExercise(exercise._id, { showInKiosk })
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleToggleSubKiosk = async (exercise, subIndex) => {
    const updatedSubs = [...exercise.subs]
    updatedSubs[subIndex] = {
      ...updatedSubs[subIndex],
      showInKiosk: !updatedSubs[subIndex].showInKiosk
    }
    
    const result = await updateExercise(exercise._id, { subs: updatedSubs })
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleUpdateSubField = async (exercise, subIndex, field, value) => {
    const updatedSubs = [...exercise.subs]
    updatedSubs[subIndex] = {
      ...updatedSubs[subIndex],
      fields: {
        ...updatedSubs[subIndex].fields,
        [field]: value
      }
    }
    
    const result = await updateExercise(exercise._id, { subs: updatedSubs })
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleDeleteSub = async (exercise, subIndex) => {
    if (!confirm('Are you sure you want to delete this subcategory?')) return
    
    const updatedSubs = exercise.subs.filter((_, i) => i !== subIndex)
    const result = await updateExercise(exercise._id, { subs: updatedSubs })
    
    if (!result.success) {
      alert(result.error)
    }
  }

  return (
    <div className="builder-container">
      <div className="builder-header">
        <div className="header-content">
          <h1 className="page-title">Exercise Builder</h1>
          <p className="page-subtitle">Create and manage your exercises, workouts, and subcategories</p>
        </div>
        <div className="builder-tabs">
          <div 
            className={`builder-tab ${tab === 'strength' ? 'active' : ''}`} 
            onClick={() => setTab('strength')}
          >
            <span className="tab-icon">💪</span>
            Strength
          </div>
          <div 
            className={`builder-tab ${tab === 'cardio' ? 'active' : ''}`} 
            onClick={() => setTab('cardio')}
          >
            <span className="tab-icon">🏃</span>
            Cardio
          </div>
          <div 
            className={`builder-tab ${tab === 'workout' ? 'active' : ''}`} 
            onClick={() => setTab('workout')}
          >
            <span className="tab-icon">📋</span>
            Workout
          </div>
        </div>
      </div>

      <div className="builder-grid">
        {/* Left column - Add new exercise */}
        <div className="builder-card create-card">
          <div className="card-header">
            <h2 className="card-title">
              Create New {tab === 'workout' ? 'Workout' : 'Exercise'}
            </h2>
            <p className="card-subtitle">
              {tab === 'workout' ? 'Build a complete workout routine' :
               tab === 'cardio' ? 'Add cardiovascular exercises' :
               'Add strength training exercises'}
            </p>
          </div>

          <div className="form-section">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  {tab === 'workout' ? 'Workout Name' : 'Exercise Name'}
                </label>
                <input 
                  className="form-input"
                  placeholder={tab === 'workout' ? 'e.g., Full Body A, Push Day' : 'e.g., Bench Press, Squats'} 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                />
              </div>
              
              {tab !== 'cardio' ? (
                <div className="form-group">
                  <label className="form-label">Weight Unit</label>
                  <select className="form-select" value={unit} onChange={e => setUnit(e.target.value)}>
                    <option value="lbs">Pounds (lbs)</option>
                    <option value="kg">Kilograms (kg)</option>
                  </select>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Primary Metric</label>
                    <select className="form-select" value={metric} onChange={e => setMetric(e.target.value)}>
                      <option value="distance">Distance</option>
                      <option value="calories">Calories</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select className="form-select" value={unit} onChange={e => setUnit(e.target.value)}>
                      {metric === 'distance' ? (
                        <>
                          <option value="m">Meters (m)</option>
                          <option value="km">Kilometers (km)</option>
                          <option value="mi">Miles (mi)</option>
                        </>
                      ) : (
                        <option value="cals">Calories</option>
                      )}
                    </select>
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label className="form-label">Visibility</label>
                <div className="toggle-wrapper">
                  <label className="toggle-label">
                    <input 
                      type="checkbox" 
                      className="toggle-input"
                      checked={showInKiosk} 
                      onChange={e => setShowInKiosk(e.target.checked)} 
                    />
                    <span className="toggle-slider"></span>
                    Show in Kiosk
                  </label>
                </div>
              </div>
            </div>

            {tab === 'workout' && (
              <div className="form-section">
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea 
                    className="form-textarea"
                    placeholder="Optional workout description or instructions" 
                    value={desc} 
                    onChange={e => setDesc(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="form-section">
              <h3 className="section-title">First Subcategory</h3>
              <p className="section-subtitle">Add an initial subcategory (you can add more later)</p>
              
              <div className="form-grid">
                <div className="form-group span-2">
                  <label className="form-label">Subcategory Name</label>
                  <input 
                    className="form-input"
                    placeholder='e.g., "10 Reps", "Main", "Week 1", or "Warmup"' 
                    value={firstSub} 
                    onChange={e => setFirstSub(e.target.value)} 
                  />
                </div>
                
                <div className="form-group span-3">
                  <label className="form-label">Available Fields</label>
                  <div className="checkbox-grid">
                    {tab !== 'cardio' ? (
                      <>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.reps} 
                            onChange={e => setFirstFields({ ...firstFields, reps: e.target.checked })} 
                          />
                          <span className="checkbox-text">Reps</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.weight} 
                            onChange={e => setFirstFields({ ...firstFields, weight: e.target.checked })} 
                          />
                          <span className="checkbox-text">Weight</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.sets} 
                            onChange={e => setFirstFields({ ...firstFields, sets: e.target.checked })} 
                          />
                          <span className="checkbox-text">Sets</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.time} 
                            onChange={e => setFirstFields({ ...firstFields, time: e.target.checked })} 
                          />
                          <span className="checkbox-text">Time</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.notes} 
                            onChange={e => setFirstFields({ ...firstFields, notes: e.target.checked })} 
                          />
                          <span className="checkbox-text">Notes</span>
                        </label>
                      </>
                    ) : (
                      <>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.primary} 
                            onChange={e => setFirstFields({ ...firstFields, primary: e.target.checked })} 
                          />
                          <span className="checkbox-text">{metric === 'distance' ? 'Distance' : 'Calories'}</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.time} 
                            onChange={e => setFirstFields({ ...firstFields, time: e.target.checked })} 
                          />
                          <span className="checkbox-text">Time</span>
                        </label>
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={firstFields.notes} 
                            onChange={e => setFirstFields({ ...firstFields, notes: e.target.checked })} 
                          />
                          <span className="checkbox-text">Notes</span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-primary" onClick={handleAddExercise}>
                <span className="btn-icon">+</span>
                Create {tab === 'workout' ? 'Workout' : 'Exercise'}
              </button>
            </div>
          </div>
        </div>

        {/* Right column - Exercise list */}
        <div className="builder-card list-card">
          <div className="card-header">
            <h2 className="card-title">
              {tab === 'workout' ? 'Workouts' : 'Exercises'}
              <span className="count-badge">({sorted.length})</span>
            </h2>
            <p className="card-subtitle">
              Manage your {tab} {tab === 'workout' ? 'routines' : 'exercises'}
            </p>
          </div>
          
          <div className="exercise-list">
            {sorted.length ? sorted.map(exercise => (
              <ExerciseListItem
                key={exercise._id}
                exercise={exercise}
                tab={tab}
                onAddSub={() => handleAddSub(exercise)}
                onDelete={() => handleDeleteExercise(exercise._id)}
                onToggleKiosk={(showInKiosk) => handleToggleKiosk(exercise, showInKiosk)}
                onToggleSubKiosk={(subIndex) => handleToggleSubKiosk(exercise, subIndex)}
                onUpdateSubField={(subIndex, field, value) => handleUpdateSubField(exercise, subIndex, field, value)}
                onDeleteSub={(subIndex) => handleDeleteSub(exercise, subIndex)}
              />
            )) : (
              <div className="empty-state">
                <div className="empty-icon">
                  {tab === 'strength' ? '💪' : tab === 'cardio' ? '🏃' : '📋'}
                </div>
                <div className="empty-title">No {tab} {tab === 'workout' ? 'workouts' : 'exercises'} yet</div>
                <div className="empty-subtitle">Create your first {tab === 'workout' ? 'workout' : 'exercise'} using the form on the left</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ExerciseListItem = ({ 
  exercise, 
  tab, 
  onAddSub, 
  onDelete, 
  onToggleKiosk, 
  onToggleSubKiosk, 
  onUpdateSubField, 
  onDeleteSub 
}) => {
  return (
    <div className="exercise-item">
      <div className="exercise-header">
        <div className="exercise-info">
          <h3 className="exercise-name">{exercise.name}</h3>
          <div className="exercise-badges">
            <span className="exercise-badge mode">
              {exercise.mode === 'strength' ? `💪 ${exercise.unit}` :
               exercise.mode === 'cardio' ? `🏃 ${exercise.cardioMetric} • ${exercise.unit || ''}` :
               `📋 workout • ${exercise.unit}`}
            </span>
            <span className={`exercise-badge kiosk ${exercise.showInKiosk ? 'visible' : 'hidden'}`}>
              {exercise.showInKiosk ? '👁️ Visible' : '🙈 Hidden'}
            </span>
          </div>
        </div>
        
        <div className="exercise-actions">
          <div className="toggle-wrapper" style={{ marginRight: '8px' }}>
            <label className="toggle-label small" style={{ fontSize: '12px', gap: '6px' }}>
              <input 
                type="checkbox" 
                className="toggle-input"
                checked={exercise.showInKiosk} 
                onChange={e => onToggleKiosk(e.target.checked)} 
              />
              <span className="toggle-slider small"></span>
              Kiosk
            </label>
          </div>
          <button 
            className="btn-secondary small" 
            onClick={onAddSub}
            style={{ 
              marginRight: '8px',
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            <span className="btn-icon">+</span>
            Add Sub
          </button>
          <button 
            className="btn-danger small" 
            onClick={onDelete}
            style={{ 
              padding: '6px 8px',
              fontSize: '12px'
            }}
          >
            <span className="btn-icon">🗑️</span>
          </button>
        </div>
      </div>

      {exercise.subs && exercise.subs.length > 0 && (
        <div className="subcategories">
          <h4 className="sub-header">Subcategories ({exercise.subs.length})</h4>
          {exercise.subs.map((sub, subIndex) => (
            <div key={subIndex} className="subcategory-item">
              <div className="sub-info">
                <div className="sub-name">{sub.name}</div>
                <span className={`sub-badge ${sub.showInKiosk !== false ? 'visible' : 'hidden'}`}>
                  {sub.showInKiosk !== false ? '👁️ Visible' : '🙈 Hidden'}
                </span>
              </div>
              
              <div className="sub-controls">
                <div className="field-checkboxes">
                  {['reps', 'weight', 'sets', 'time', 'primary', 'notes'].map(field => (
                    <label key={field} className={`field-checkbox ${(sub.fields?.[field]) !== false ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={(sub.fields?.[field]) !== false}
                        onChange={e => onUpdateSubField(subIndex, field, e.target.checked)}
                      />
                      <span className="field-name">
                        {field}
                      </span>
                    </label>
                  ))}
                </div>
                
                <div className="sub-actions">
                  <label className="toggle-label small">
                    <input 
                      type="checkbox" 
                      className="toggle-input"
                      checked={sub.showInKiosk !== false} 
                      onChange={() => onToggleSubKiosk(subIndex)} 
                    />
                    <span className="toggle-slider small"></span>
                    Show in Kiosk
                  </label>
                  
                  <button 
                    className="btn-danger tiny" 
                    onClick={() => onDeleteSub(subIndex)}
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Builder
